import { Component, Input, OnInit, ChangeDetectorRef } from '@angular/core';
import { ModalController, NavParams, ToastController } from '@ionic/angular';
import { ShippingService } from 'src/app/services/shipping.service';
import { PaymentService } from 'src/app/services/shipment-payment.service';
import { ProductModel } from 'src/app/models/wardrobe.model';
import { Stripe, StripeElements, StripeIdealBankElement } from '@stripe/stripe-js';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-pay-shipping-with-mollie',
  templateUrl: './pay-shipping-with-mollie.component.html',
  styleUrls: ['./pay-shipping-with-mollie.component.scss'],
  host: { class: 'ship-modal' }
})
export class PayShippingWithMollieComponent implements OnInit {
  @Input() message: string;
  @Input() userId!: number;
  @Input() productId!: number;
  @Input() offered_product_id!: number;
  @Input() product!: ProductModel;
  @Input() chatId!: string;
  @Input() userType!: string;
  @Input() formSubmittedSuccessfully!: boolean;

  isModalOpen = true;
  isLoading = false;
  orderType = '';
  saveAddress = 0;

  shippingMethods: any[] = [];
  selectedMethodId: string | null = null;
  selectedShippingPrice = 0;
  ShippingMaxWeight = 0;

  /** Store original method from API to compare */
  originalMethodId: string | null = null;
  mustSelectAgain = false;

  stripe: Stripe | null = null;
  elements!: StripeElements;
  ideal!: StripeIdealBankElement;

  showIdealUI = false;

  constructor(
    private navParams: NavParams,
    private modalCtrl: ModalController,
    private sendCloudService: ShippingService,
    private paymentService: PaymentService,
    private cdRef: ChangeDetectorRef,
    private toastController: ToastController,
    protected translate: TranslateService
  ) {
    this.orderType = this.navParams.get('orderType') || '';
    console.log('Order Type:', this.orderType);
  }

  async ngOnInit() {
    if (!this.productId || !this.userId) {
      console.error('Missing productId or userId');
      return;
    }

    this.isLoading = true;

    try {
    // Step 1: Get shipping method ID from API
      const response = await firstValueFrom(
        this.sendCloudService.getShippingData(this.userType, this.userId, this.productId, null)
      );

      this.originalMethodId = response?.shipping_details?.shipping_method_id || null;
      this.selectedMethodId = this.originalMethodId;
      console.log('Original shipping method ID from backend:', this.originalMethodId);

      // Fetch available shipping methods & check mismatch
      this.fetchShippingMethods();
    } catch (error) {
      console.error('Failed to load shipping details:', error);
      this.isLoading = false;
    }
  }

  fetchShippingMethods(): void {
    this.sendCloudService.getShippingMethods().subscribe({
      next: (res: any) => {
        this.shippingMethods = res?.shipping_methods || [];

        const selectedMethod = this.shippingMethods.find(
          (m: any) => m.id === this.selectedMethodId
        );

        if (selectedMethod) {
          this.selectedShippingPrice = parseFloat(selectedMethod.price || '0');
          this.ShippingMaxWeight = parseFloat(selectedMethod.max_weight || '0');
        }

        // Force re-selection if method IDs differ
        if (this.originalMethodId && this.selectedMethodId !== this.originalMethodId) {
          this.mustSelectAgain = true;
          console.warn('Shipping method mismatch — must select again.');
        }

        this.isLoading = false;
        this.cdRef.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching shipping methods', err);
        this.isLoading = false;
      },
    });
  }

  onMethodChange(event: any) {
    this.selectedMethodId = event.detail.value;
    this.mustSelectAgain = false; // reset warning after reselect

    const selected = this.shippingMethods.find((m: any) => m.id === this.selectedMethodId);
    if (selected) {
      this.selectedShippingPrice = parseFloat(selected.price || '0');
      this.ShippingMaxWeight = parseFloat(selected.max_weight || '0');
    }
  }

  async submitPayment() {
    // Require reselection if mismatch still exists
    if (this.originalMethodId && this.selectedMethodId !== this.originalMethodId) {
      this.mustSelectAgain = true;
      const toast = await this.toastController.create({
        message: 'Shipping method has changed. Please select again before paying.',
        duration: 3000,
        color: 'warning',
      });
      await toast.present();
      return;
    }

    if (!this.selectedMethodId) {
      const toast = await this.toastController.create({
        message: 'Please select a shipping method first.',
        duration: 3000,
        color: 'danger',
      });
      await toast.present();
      return;
    }

    this.isLoading = true;

    try {
      const mollieResponse = await firstValueFrom(
        this.paymentService.startMolliePayment({
          user_id: this.userId,
          product_id: this.productId,
          shipping_method_id: this.selectedMethodId,
          type: this.userType,
        })
      );
      const redirectUrl = mollieResponse?.redirect_url;
      if (redirectUrl) {
        await this.modalCtrl.dismiss({ isModalOpen: false, redirecting: true });
        window.open(redirectUrl, '_system');
      } else {
        console.error('Missing redirect URL');
      }
    } catch (error) {
      console.error('Error during payment process:', error);
      const toast = await this.toastController.create({
        message: 'Payment failed. Please try again.',
        duration: 3000,
        color: 'danger',
      });
      await toast.present();
    } finally {
      this.isLoading = false;
    }
  }

  dismiss() {
    this.modalCtrl.dismiss({ isModalOpen: false });
  }

  ionViewWillLeave() {
    this.isModalOpen = false;
  }
}
